import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@utils/axios_api";
import { downloadExcel } from "@utils/downloadExcel";
import { qk } from "@utils/queryKey";

const fetchAttendance   = (params) => get("/admin/lectureAttend/attendList",  { params });
const fetchLectureInfo  = (params) => get("/admin/lectureAttend/lectureInfo", { params });

export function useLectureAttend(lectureId) {
  const queryClient = useQueryClient();
  const { data: lectureData } = useQuery({
    queryKey: qk.lectureInfo(lectureId),
    queryFn : () => fetchLectureInfo({ lectureId }),
    enabled : !!lectureId,
  });

  const dayInfo = useMemo(() => lectureData?.info?.dayInfo ?? [],[lectureData]);
  const dates = useMemo(() => dayInfo.map(d => d.eduDt), [dayInfo]);

  const [eduDt, setEduDt] = useState(null);

  useEffect(() => {
    if (!eduDt && dates.length) setEduDt(dates[0]);
  }, [dates, eduDt]);

  const { data: attendData, refetch } = useQuery({
    queryKey: [...qk.attendList(lectureId), eduDt],
    queryFn : () => fetchAttendance({ lectureId, eduDt }),
    enabled : !!lectureId && !!eduDt,
  });

  const getClassCnt = dt => {
    const obj = dayInfo.find(d => d.eduDt === dt);
    return obj ? obj.classCnt : 0;
  };

  const classCnt = getClassCnt(eduDt);
  const classNos = Array.from({ length: classCnt }, (_, i) => String(i + 1));

  const [selectedRows, setSelectedRows] = useState([]); // memberId[]

  const toggleRow = (id) =>
    setSelectedRows((p) => (p.includes(id) ? p.filter((v) => v !== id) : [...p, id]));

  const selectAllRows = (checked) =>
    setSelectedRows(
      checked && attendData ? attendData.map((r) => r.memberId) : []
    );

  const bulkUpdate = async ({ memberIdList, classNos, attendYn }) => {
    await post("/admin/lectureAttend/bulkUpdate", {
      lectureId,
      eduDt,
      memberIdList,
      classNos,
      attendYn, // 'Y' or 'N'
    });
    refetch();
    queryClient.invalidateQueries({ queryKey: qk.studentList(lectureId) });
    // queryClient.invalidateQueries(["selectAdminLectureStudentList", lectureId]);
  };

  const bulkAttend = () => {
    if (!selectedRows.length) {
      alert("출석할 수강생을 선택해주세요.");
      return;
    }
    bulkUpdate({ memberIdList: selectedRows, classNos, attendYn: "Y" });
  }
  const bulkAbsent = () => {
    if (!selectedRows.length) {
      alert("결석할 수강생을 선택해주세요.");
      return;
    }
    bulkUpdate({ memberIdList: selectedRows, classNos, attendYn: "N" });
  }


  const handleExcelDownload = () => {
    const fileName = `${lectureData.info.lectureNm}_출석부_${eduDt.replace(/-/g, "")}`;
    downloadExcel({
      url: "/admin/lectureAttend/attendListExcelDown",
      params: {lectureId, eduDt},
      customFilename: fileName,
    });

  }


  const toggleCell = (memberId, classNo, flag) =>
    bulkUpdate({ memberIdList: [memberId], classNos: [classNo], attendYn: flag });

  const allRowsChecked =
    attendData?.length &&
    selectedRows.length === attendData.length;

  return {
    dates, eduDt, setEduDt,
    classNos,
    attendData, lectureData,
    selectedRows,  allRowsChecked,
    toggleRow, selectAllRows,
    bulkAttend, bulkAbsent, toggleCell, handleExcelDownload
  };
}
